import { LangCode } from "@/lib/gql/__generated__/graphql";

import { useEditedQuestionIndicator, useErrorQuestionIndicator } from "../../../../_shared/hooks";
import { useSendCustomMailSettingForCheckInBefore_00days } from "../../../api/send-custom-mail-setting/check-in-before-00-days";
import { useSendCustomMailSettingForCheckInBefore_01days } from "../../../api/send-custom-mail-setting/check-in-before-01-days";
import { useSendCustomMailSettingForCheckInBefore_07days } from "../../../api/send-custom-mail-setting/check-in-before-07-days";
import { MailTemplateItem } from "../_shared/mail-template-item";

import type { BasicCustomMailSettingFormData } from "../../../schema";
import type { UseFormReturn } from "react-hook-form";

type Props = {
	form: UseFormReturn<BasicCustomMailSettingFormData>;
	hotelID: string;
	hotelEmailAddress: string;
};

export const ForCheckInRemind = ({ form, hotelID, hotelEmailAddress }: Props) => {
	const {
		watch,
		setValue,
		formState: { errors },
	} = form;

	// フォームの値を監視
	const oneWeekBeforeOption = watch("forReservation.atCheckInBefore_07days.shouldSendEmail");
	const oneDayBeforeOption = watch("forReservation.atCheckInBefore_01days.shouldSendEmail");
	const todayOption = watch("forReservation.atCheckInBefore_00days.shouldSendEmail");

	const oneWeekBeforeValue = watch("forReservation.atCheckInBefore_07days.text");
	const oneDayBeforeValue = watch("forReservation.atCheckInBefore_01days.text");
	const todayValue = watch("forReservation.atCheckInBefore_00days.text");

	// 値が入っているかどうかを監視（設定した項目がある場合に青丸を表示）
	useEditedQuestionIndicator("1週間前", Boolean(oneWeekBeforeValue) || oneWeekBeforeOption);
	useEditedQuestionIndicator("前日", Boolean(oneDayBeforeValue) || oneDayBeforeOption);
	useEditedQuestionIndicator("当日", Boolean(todayValue) || todayOption);

	// バリデーションエラーがある場合に赤丸を表示
	useErrorQuestionIndicator("1週間前", Boolean(errors.forReservation?.atCheckInBefore_07days?.text));
	useErrorQuestionIndicator("前日", Boolean(errors.forReservation?.atCheckInBefore_01days?.text));
	useErrorQuestionIndicator("当日", Boolean(errors.forReservation?.atCheckInBefore_00days?.text));

	// テストメール送信ハンドラー
	const { sendTestMailAtCheckInBefore07Days } = useSendCustomMailSettingForCheckInBefore_07days(); // 1週間前
	const { sendTestMailAtCheckInBefore01Days } = useSendCustomMailSettingForCheckInBefore_01days(); // 1日前
	const { sendTestMailAtCheckInBefore00Days } = useSendCustomMailSettingForCheckInBefore_00days(); // 当日

	const handleSendTestEmailForOneWeekBefore = async ({ email }: { email: string }) => {
		await sendTestMailAtCheckInBefore07Days({
			email,
			content: oneWeekBeforeValue || "",
			hotelId: hotelID,
			langCode: LangCode.Default,
		});
	};

	const handleSendTestEmailForOneDayBefore = async ({ email }: { email: string }) => {
		await sendTestMailAtCheckInBefore01Days({
			email,
			content: oneDayBeforeValue || "",
			hotelId: hotelID,
			langCode: LangCode.Default,
		});
	};

	const handleSendTestEmailForToday = async ({ email }: { email: string }) => {
		await sendTestMailAtCheckInBefore00Days({
			email,
			content: todayValue || "",
			hotelId: hotelID,
			langCode: LangCode.Default,
		});
	};

	return (
		<div className="space-y-6">
			<div className="ml-2">
				{/* 一週間前 */}
				<MailTemplateItem
					hidePreviewButton={false}
					label={"1週間前"}
					dialogTitle={"1週間前"}
					value={oneWeekBeforeValue || ""}
					errorMessage={errors.forReservation?.atCheckInBefore_07days?.text?.message || ""}
					handleChange={(value) =>
						setValue(
							"forReservation.atCheckInBefore_07days.text", //
							value,
							{ shouldDirty: true, shouldValidate: true },
						)
					}
					sendTestEmail={handleSendTestEmailForOneWeekBefore}
					lang={LangCode.Default}
					hotelEmailAddress={hotelEmailAddress}
					radioOptions={[
						{ label: "送信する", value: true, isSelected: oneWeekBeforeOption },
						{ label: "送信しない", value: false, isSelected: !oneWeekBeforeOption },
					]}
					onRadioChange={(value) =>
						setValue(
							"forReservation.atCheckInBefore_07days.shouldSendEmail", //
							value,
							{ shouldDirty: true, shouldValidate: true },
						)
					}
				/>

				{/* 前日 */}
				<MailTemplateItem
					hidePreviewButton={false}
					label={"前日"}
					dialogTitle={"前日"}
					value={oneDayBeforeValue || ""}
					errorMessage={errors.forReservation?.atCheckInBefore_01days?.text?.message || ""}
					handleChange={(value) =>
						setValue(
							"forReservation.atCheckInBefore_01days.text", //
							value,
							{ shouldDirty: true, shouldValidate: true },
						)
					}
					sendTestEmail={handleSendTestEmailForOneDayBefore}
					lang={LangCode.Default}
					hotelEmailAddress={hotelEmailAddress}
					radioOptions={[
						{ label: "送信する", value: true, isSelected: oneDayBeforeOption },
						{ label: "送信しない", value: false, isSelected: !oneDayBeforeOption },
					]}
					onRadioChange={(value) =>
						setValue(
							"forReservation.atCheckInBefore_01days.shouldSendEmail", //
							value,
							{ shouldDirty: true, shouldValidate: true },
						)
					}
				/>

				{/* 当日 */}
				<MailTemplateItem
					hidePreviewButton={false}
					label={"当日"}
					dialogTitle={"当日"}
					value={todayValue || ""}
					errorMessage={errors.forReservation?.atCheckInBefore_00days?.text?.message || ""}
					handleChange={(value) =>
						setValue(
							"forReservation.atCheckInBefore_00days.text", //
							value,
							{ shouldDirty: true, shouldValidate: true },
						)
					}
					sendTestEmail={handleSendTestEmailForToday}
					lang={LangCode.Default}
					hotelEmailAddress={hotelEmailAddress}
					radioOptions={[
						{ label: "送信する", value: true, isSelected: todayOption },
						{ label: "送信しない", value: false, isSelected: !todayOption },
					]}
					onRadioChange={(value) =>
						setValue(
							"forReservation.atCheckInBefore_00days.shouldSendEmail", //
							value,
							{ shouldDirty: true, shouldValidate: true },
						)
					}
				/>
			</div>
		</div>
	);
};
